import React, { createContext, useContext, useState, useEffect, type ReactNode } from 'react';

import { useOperatorAuth } from '@contexts/operator/OperatorAuthContext';
import { useOperatorData } from '@contexts/operator/OperatorDataContext';

interface TutorialStep {
  key: string;
  type: 'info' | 'action';
  navigateTo?: string;
  formPath?: string;
  successPattern?: RegExp;
  requiresCompletion?: boolean;
}

interface TutorialFlow {
  key: string;
  steps: TutorialStep[];
}

interface OnboardingContextValue {
  showTutorialSelection: boolean;
  showOnboarding: boolean;
  selectedTutorial: string | null;
  currentStep: number;
  loading: boolean;
  openTutorialSelection: () => void;
  closeTutorialSelection: () => void;
  startTutorial: (tutorialKey: string) => void;
  skipAllTutorials: () => void;
  completeOnboarding: () => void;
  skipOnboarding: () => void;
  resetOnboarding: () => void;
  nextStep: () => void;
  prevStep: () => void;
  goToStep: (step: number) => void;
  completedTasks: string[];
  markTaskCompleted: (taskKey: string) => void;
  markTaskCompletedAndNext: (taskKey: string) => void;
  clearCompletedTasks: () => void;
}

const OnboardingContext = createContext<OnboardingContextValue | null>(null);

const ONBOARDING_STORAGE_KEY = 'gowarehouse_operator_onboarding_completed';
const COMPLETED_TASKS_STORAGE_KEY = 'gowarehouse_operator_completed_tasks';

// 五種教學流程定義
export const TUTORIAL_FLOWS: Record<string, TutorialFlow> = {
  warehouse_setup: {
    key: 'warehouse_setup',
    steps: [
      { key: 'welcome', type: 'info', requiresCompletion: false },
      { key: 'create_shelf', type: 'action', navigateTo: '/shelves', formPath: '/shelves/batch_new', successPattern: /^\/shelves\/\d+$/, requiresCompletion: true },
      { key: 'create_inventory_type', type: 'action', navigateTo: '/data/inventory_types', requiresCompletion: false },
      { key: 'create_shipping_account', type: 'action', navigateTo: '/system_settings/shipping_accounts', requiresCompletion: false },
      { key: 'complete', type: 'info', requiresCompletion: false },
    ],
  },
  merchant_setup: {
    key: 'merchant_setup',
    steps: [
      { key: 'welcome', type: 'info', requiresCompletion: false },
      { key: 'create_merchant', type: 'action', navigateTo: '/merchants', formPath: '/merchants/new', successPattern: /^\/merchants\/\d+$/, requiresCompletion: true },
      { key: 'create_product', type: 'action', navigateTo: '/products', formPath: '/products/new', successPattern: /^\/products\/\d+$/, requiresCompletion: true },
      { key: 'create_sales_channel', type: 'action', navigateTo: '/data/sales_channels', formPath: '/data/sales_channels/new', successPattern: /^\/data\/sales_channels\/\d+$/, requiresCompletion: true },
      { key: 'setup_merchant_inventory_types', type: 'action', navigateTo: '/data/merchant_inventory_types', requiresCompletion: false },
      { key: 'complete', type: 'info', requiresCompletion: false },
    ],
  },
  inbound_process: {
    key: 'inbound_process',
    steps: [
      { key: 'welcome', type: 'info', requiresCompletion: false },
      { key: 'create_inbound', type: 'action', navigateTo: '/inbounds', formPath: '/inbounds/new', successPattern: /^\/inbounds\/\d+$/, requiresCompletion: true },
      { key: 'acceptance', type: 'action', navigateTo: '/operations/inbound_acceptance', requiresCompletion: false },
      { key: 'putaway', type: 'info', requiresCompletion: false },
      { key: 'complete', type: 'info', requiresCompletion: false },
    ],
  },
  outbound_process: {
    key: 'outbound_process',
    steps: [
      { key: 'welcome', type: 'info', requiresCompletion: false },
      { key: 'create_order', type: 'action', navigateTo: '/orders', formPath: '/orders/new', successPattern: /^\/orders\/\d+$/, requiresCompletion: true },
      { key: 'create_picking_list', type: 'action', navigateTo: '/picking_lists/generate', requiresCompletion: false },
      { key: 'fulfillment_qc', type: 'action', navigateTo: '/operations/fulfillment_qc', requiresCompletion: false },
      { key: 'complete', type: 'info', requiresCompletion: false },
    ],
  },
  advanced: {
    key: 'advanced',
    steps: [
      { key: 'welcome', type: 'info', requiresCompletion: false },
      { key: 'create_operator', type: 'action', navigateTo: '/operators', formPath: '/operators/new', successPattern: /^\/operators$/, requiresCompletion: false },
      { key: 'stock_taking', type: 'action', navigateTo: '/stock_takings', formPath: '/stock_takings/new', successPattern: /^\/stock_takings\/\d+$/, requiresCompletion: false },
      { key: 'return_order', type: 'action', navigateTo: '/return_orders', formPath: '/return_orders/new', successPattern: /^\/return_orders\/\d+$/, requiresCompletion: false },
      { key: 'work_order', type: 'action', navigateTo: '/work_orders', formPath: '/work_orders/new', successPattern: /^\/work_orders\/\d+$/, requiresCompletion: false },
      { key: 'complete', type: 'info', requiresCompletion: false },
    ],
  },
};

interface OnboardingProviderProps {
  children: ReactNode;
}

export const OnboardingProvider = ({ children }: OnboardingProviderProps) => {
  const { operator, loading: authLoading } = useOperatorAuth();
  const { merchants: _merchants, loading: dataLoading } = useOperatorData();

  const [showTutorialSelection, setShowTutorialSelection] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [selectedTutorial, setSelectedTutorial] = useState<string | null>(null);
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(true);
  const [completedTasks, setCompletedTasks] = useState<string[]>([]);

  // Check onboarding status - only load completed tasks, no auto-popup
  useEffect(() => {
    if (!authLoading && !dataLoading && operator) {
      const tasksKey = `${COMPLETED_TASKS_STORAGE_KEY}_${operator.id}`;
      const tasksData = localStorage.getItem(tasksKey);

      // Load completed tasks
      if (tasksData) {
        try {
          setCompletedTasks(JSON.parse(tasksData));
        } catch (e) {
          setCompletedTasks([]);
        }
      }

      setLoading(false);
    }
  }, [authLoading, dataLoading, operator]);

  const openTutorialSelection = () => {
    setShowTutorialSelection(true);
  };

  const closeTutorialSelection = () => {
    setShowTutorialSelection(false);
  };

  const startTutorial = (tutorialKey: string) => {
    setSelectedTutorial(tutorialKey);
    setShowTutorialSelection(false);
    setShowOnboarding(true);
    setCurrentStep(0);
  };

  const skipAllTutorials = () => {
    if (operator) {
      const storageKey = `${ONBOARDING_STORAGE_KEY}_${operator.id}`;
      localStorage.setItem(storageKey, 'true');
      setShowTutorialSelection(false);
      setShowOnboarding(false);
    }
  };

  const completeOnboarding = () => {
    if (operator) {
      const storageKey = `${ONBOARDING_STORAGE_KEY}_${operator.id}`;
      localStorage.setItem(storageKey, 'true');
      setShowOnboarding(false);
      setCurrentStep(0);
      setSelectedTutorial(null);
    }
  };

  const skipOnboarding = () => {
    setShowOnboarding(false);
    setCurrentStep(0);
    setSelectedTutorial(null);
  };

  const resetOnboarding = () => {
    if (operator) {
      const storageKey = `${ONBOARDING_STORAGE_KEY}_${operator.id}`;
      localStorage.removeItem(storageKey);
      setShowTutorialSelection(true);
      setShowOnboarding(false);
      setCurrentStep(0);
      setSelectedTutorial(null);
    }
  };

  const nextStep = () => {
    setCurrentStep(prev => prev + 1);
  };

  const prevStep = () => {
    setCurrentStep(prev => Math.max(0, prev - 1));
  };

  const goToStep = (step: number) => {
    setCurrentStep(step);
  };

  const markTaskCompleted = (taskKey: string) => {
    if (operator && !completedTasks.includes(taskKey)) {
      const newCompletedTasks = [...completedTasks, taskKey];
      setCompletedTasks(newCompletedTasks);

      const tasksKey = `${COMPLETED_TASKS_STORAGE_KEY}_${operator.id}`;
      localStorage.setItem(tasksKey, JSON.stringify(newCompletedTasks));
    }
  };

  const markTaskCompletedAndNext = (taskKey: string) => {
    markTaskCompleted(taskKey);
    if (showOnboarding) {
      nextStep();
    }
  };

  const clearCompletedTasks = () => {
    if (operator) {
      setCompletedTasks([]);
      const tasksKey = `${COMPLETED_TASKS_STORAGE_KEY}_${operator.id}`;
      localStorage.removeItem(tasksKey);
    }
  };

  const value: OnboardingContextValue = {
    showTutorialSelection,
    showOnboarding,
    selectedTutorial,
    currentStep,
    loading,
    openTutorialSelection,
    closeTutorialSelection,
    startTutorial,
    skipAllTutorials,
    completeOnboarding,
    skipOnboarding,
    resetOnboarding,
    nextStep,
    prevStep,
    goToStep,
    completedTasks,
    markTaskCompleted,
    markTaskCompletedAndNext,
    clearCompletedTasks,
  };

  return (
    <OnboardingContext.Provider value={value}>
      {children}
    </OnboardingContext.Provider>
  );
};

export function useOnboarding(): OnboardingContextValue {
  const context = useContext(OnboardingContext);
  if (!context) {
    throw new Error('useOnboarding must be used within OnboardingProvider');
  }
  return context;
}

// Safe version that doesn't throw - returns default values when not in provider
export function useSafeOnboarding(): OnboardingContextValue | { showOnboarding: false } {
  const context = useContext(OnboardingContext);
  return context || { showOnboarding: false };
}
